import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { TestResult } from '../types';
import { logAuditEvent } from '../services/consentService';

interface ExportData {
  'Fecha': string;
  'Paciente': string;
  'Test': string;
  'Categoría': string;
  'Puntuación': number;
  'Severidad': string;
  'Notas': string;
}

/**
 * Format test results for export
 */
function formatDataForExport(results: TestResult[]): ExportData[] {
  return results.map(result => ({
    'Fecha': new Date(result.date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
    'Paciente': result.patientName || 'Anónimo',
    'Test': result.testCode,
    'Categoría': result.testCategory,
    'Puntuación': result.totalScore,
    'Severidad': result.severity || 'N/A',
    'Notas': result.notes || '',
  }));
}

/**
 * Export results to Excel (.xlsx)
 */
export function exportToExcel(results: TestResult[], filename?: string) {
  // GDPR: Log data export to audit trail (async, non-blocking)
  logAuditEvent('data_export', 'data_export', {
    format: 'excel',
    recordCount: results.length
  }).catch(() => {}); // Silent fail - don't block export

  const exportData = formatDataForExport(results);

  // Create workbook
  const wb = XLSX.utils.book_new();

  // Create worksheet from data
  const ws = XLSX.utils.json_to_sheet(exportData);

  // Set column widths
  const colWidths = [
    { wch: 25 }, // Fecha
    { wch: 20 }, // Paciente
    { wch: 15 }, // Test
    { wch: 18 }, // Categoría
    { wch: 12 }, // Puntuación
    { wch: 15 }, // Severidad
    { wch: 40 }, // Notas
  ];
  ws['!cols'] = colWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Resultados');

  // Generate Excel file
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  // Save file
  const finalFilename = filename || `resultados_${new Date().toISOString().split('T')[0]}.xlsx`;
  saveAs(data, finalFilename);

  return finalFilename;
}

/**
 * Export results to CSV
 */
export function exportToCSV(results: TestResult[], filename?: string) {
  // GDPR: Log data export to audit trail
  logAuditEvent('data_export', 'data_export', {
    format: 'csv',
    recordCount: results.length
  }).catch(() => {});

  const exportData = formatDataForExport(results);

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(exportData);

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Resultados');

  // Generate CSV
  const csvContent = XLSX.utils.sheet_to_csv(ws);
  const data = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Save file
  const finalFilename = filename || `resultados_${new Date().toISOString().split('T')[0]}.csv`;
  saveAs(data, finalFilename);

  return finalFilename;
}

/**
 * Export results to JSON
 */
export function exportToJSON(results: TestResult[], filename?: string) {
  // GDPR: Log data export to audit trail
  logAuditEvent('data_export', 'data_export', {
    format: 'json',
    recordCount: results.length
  }).catch(() => {});

  const jsonContent = JSON.stringify(results, null, 2);
  const data = new Blob([jsonContent], { type: 'application/json' });

  // Save file
  const finalFilename = filename || `resultados_${new Date().toISOString().split('T')[0]}.json`;
  saveAs(data, finalFilename);

  return finalFilename;
}

/**
 * Export results to PDF (basic implementation)
 * Note: This creates a simple text-based PDF. For more advanced PDF generation,
 * consider using a library like jsPDF or pdfmake
 */
export function exportToPDF(results: TestResult[], filename?: string) {
  // GDPR: Log data export to audit trail
  logAuditEvent('data_export', 'data_export', {
    format: 'pdf',
    recordCount: results.length
  }).catch(() => {});

  const exportData = formatDataForExport(results);

  // Create formatted text content
  let textContent = 'RESULTADOS DE EVALUACIONES PSICOMÉTRICAS\n\n';
  textContent += `Generado: ${new Date().toLocaleString('es-ES')}\n`;
  textContent += `Total de registros: ${results.length}\n\n`;
  textContent += '='.repeat(80) + '\n\n';

  exportData.forEach((row, index) => {
    textContent += `#${index + 1}\n`;
    textContent += `Fecha: ${row.Fecha}\n`;
    textContent += `Paciente: ${row.Paciente}\n`;
    textContent += `Test: ${row.Test}\n`;
    textContent += `Categoría: ${row.Categoría}\n`;
    textContent += `Puntuación: ${row.Puntuación}\n`;
    textContent += `Severidad: ${row.Severidad}\n`;
    if (row.Notas) {
      textContent += `Notas: ${row.Notas}\n`;
    }
    textContent += '\n' + '-'.repeat(80) + '\n\n';
  });

  // Save as text file (can be converted to PDF manually or with server-side tools)
  const data = new Blob([textContent], { type: 'text/plain;charset=utf-8;' });
  const finalFilename = filename || `resultados_${new Date().toISOString().split('T')[0]}.txt`;
  saveAs(data, finalFilename);

  return finalFilename;
}

/**
 * Get file size estimate
 */
export function getExportSizeEstimate(results: TestResult[], format: 'xlsx' | 'csv' | 'json'): string {
  const avgBytesPerResult = {
    xlsx: 200,
    csv: 150,
    json: 300,
  };

  const estimatedBytes = results.length * avgBytesPerResult[format];

  if (estimatedBytes < 1024) {
    return `${estimatedBytes} B`;
  } else if (estimatedBytes < 1024 * 1024) {
    return `${(estimatedBytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(estimatedBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
